// BMI Calculation Utilities

/**
 * Calculate BMI from weight (kg) and height (cm)
 */
export const calculateBMI = (weightKg, heightCm) => {
  const heightM = heightCm / 100;
  return weightKg / (heightM * heightM);
};

/**
 * Convert lbs to kg
 */
export const lbsToKg = (lbs) => lbs * 0.453592;

/**
 * Convert kg to lbs
 */
export const kgToLbs = (kg) => kg / 0.453592;

/**
 * Convert feet+inches to cm
 */
export const ftInToCm = (feet, inches) => {
  const totalInches = (feet * 12) + inches;
  return totalInches * 2.54;
};

/**
 * Convert cm to feet+inches
 */
export const cmToFtIn = (cm) => {
  const totalInches = cm / 2.54;
  const feet = Math.floor(totalInches / 12);
  const inches = Math.round(totalInches % 12);
  return { feet, inches };
};

/**
 * Get WHO BMI Category
 */
export const getWHOCategory = (bmi) => {
  if (bmi < 16) return { label: 'Severely Underweight', color: 'info', severity: 'severe' };
  if (bmi < 18.5) return { label: 'Underweight', color: 'info', severity: 'mild' };
  if (bmi < 25) return { label: 'Normal', color: 'success', severity: 'normal' };
  if (bmi < 30) return { label: 'Overweight', color: 'warning', severity: 'mild' };
  if (bmi < 35) return { label: 'Obese Class I', color: 'destructive', severity: 'moderate' };
  if (bmi < 40) return { label: 'Obese Class II', color: 'destructive', severity: 'severe' };
  return { label: 'Obese Class III', color: 'destructive', severity: 'extreme' };
};

/**
 * Get Athlete Performance Band
 */
export const getAthletePerformanceBand = (bmi) => {
  if (bmi < 18.5) {
    return {
      label: 'Underfueling / Injury Risk',
      description: 'BMI suggests potential underfueling which may increase injury risk and reduce performance.',
      color: 'info',
      icon: 'AlertTriangle'
    };
  }
  if (bmi < 22.5) {
    return {
      label: 'Optimal Endurance Zone',
      description: 'Ideal range for endurance athletes. Good power-to-weight ratio for running, cycling, etc.',
      color: 'success',
      icon: 'Zap'
    };
  }
  if (bmi < 25) {
    return {
      label: 'Hybrid / Power Endurance',
      description: 'Suitable for athletes requiring both strength and endurance. Common in team sports.',
      color: 'success',
      icon: 'Activity'
    };
  }
  if (bmi < 28) {
    return {
      label: 'Strength Bias / Muscular Build',
      description: 'Often seen in strength-focused athletes. BMI may not reflect true body composition.',
      color: 'warning',
      icon: 'Dumbbell'
    };
  }
  return {
    label: 'Possible Excess Mass',
    description: 'Higher BMI may indicate excess mass. Consider body composition assessment.',
    color: 'warning',
    icon: 'Scale'
  };
};

/**
 * Calculate Waist-to-Height Ratio
 */
export const calculateWaistToHeight = (waistCm, heightCm) => {
  return waistCm / heightCm;
};

/**
 * Get Waist-to-Height Risk Level
 */
export const getWaistToHeightRisk = (ratio) => {
  if (ratio < 0.4) return { label: 'Very Low Risk', color: 'success', description: 'Excellent waist-to-height ratio' };
  if (ratio < 0.5) return { label: 'Low Risk', color: 'success', description: 'Healthy waist-to-height ratio' };
  if (ratio < 0.6) return { label: 'Elevated Risk', color: 'warning', description: 'Consider lifestyle modifications' };
  return { label: 'High Risk', color: 'destructive', description: 'Higher health risk - consult healthcare provider' };
};

/**
 * Check if lean mass bias warning should show
 */
export const shouldShowLeanMassBias = (bmi, waistToHeightRatio) => {
  return bmi > 25 && waistToHeightRatio !== null && waistToHeightRatio < 0.5;
};

/**
 * Calculate BMI Prime (ratio to upper limit of normal BMI)
 */
export const calculateBMIPrime = (bmi) => {
  return bmi / 25;
};

/**
 * Calculate Ponderal Index (Rohrer's Index)
 */
export const calculatePonderalIndex = (weightKg, heightCm) => {
  const heightM = heightCm / 100;
  return weightKg / Math.pow(heightM, 3);
};

/**
 * Calculate healthy weight range for a given height
 */
export const calculateHealthyWeightRange = (heightCm) => {
  const heightM = heightCm / 100;
  const minWeight = 18.5 * heightM * heightM;
  const maxWeight = 24.9 * heightM * heightM;
  return { min: minWeight, max: maxWeight };
};

/**
 * Calculate healthy BMI range (always 18.5-24.9)
 */
export const getHealthyBMIRange = () => {
  return { min: 18.5, max: 24.9 };
};

/**
 * Estimate running pace impact per kg change
 * Approximately 1-2 seconds per km per kg
 */
export const estimatePaceImpact = (weightChange) => {
  const minImpact = Math.abs(weightChange) * 1;
  const maxImpact = Math.abs(weightChange) * 2;
  const direction = weightChange > 0 ? 'slower' : 'faster';
  return { minImpact, maxImpact, direction };
};

/**
 * Estimate hydration needs (ml per hour during exercise)
 */
export const estimateHydrationNeeds = (weightKg, temperature = 'warm') => {
  // Base: 400-800ml per hour, adjusted by weight and temperature
  const baseRate = weightKg * 7; // ~7ml per kg per hour base
  
  const tempMultipliers = {
    cool: 0.8,
    warm: 1.0,
    hot: 1.3
  };
  
  const multiplier = tempMultipliers[temperature] || 1.0;
  const recommended = baseRate * multiplier;
  
  return {
    min: Math.round(recommended * 0.8),
    max: Math.round(recommended * 1.2),
    recommended: Math.round(recommended),
    warning: temperature === 'hot' ? 'Increase intake and monitor for signs of dehydration' : null
  };
};

/**
 * Get BMI scale position (0-100%)
 */
export const getBMIScalePosition = (bmi) => {
  // Scale from 15 to 40 BMI mapped to 0-100%
  const minBMI = 15;
  const maxBMI = 40;
  const position = ((bmi - minBMI) / (maxBMI - minBMI)) * 100;
  return Math.max(0, Math.min(100, position));
};

/**
 * Format BMI value
 */
export const formatBMI = (bmi) => {
  return bmi.toFixed(1);
};

/**
 * Format weight
 */
export const formatWeight = (kg, unit = 'metric') => {
  if (unit === 'us') {
    return `${kgToLbs(kg).toFixed(1)} lbs`;
  }
  return `${kg.toFixed(1)} kg`;
};

/**
 * Validate age
 */
export const validateAge = (age) => {
  const numAge = Number(age);
  if (isNaN(numAge)) return { valid: false, message: 'Please enter a valid age' };
  if (numAge < 2) return { valid: false, message: 'Age must be at least 2 years' };
  if (numAge > 120) return { valid: false, message: 'Please enter a valid age under 120' };
  return { valid: true, message: '' };
};

/**
 * Validate weight (kg)
 */
export const validateWeight = (weight) => {
  const numWeight = Number(weight);
  if (isNaN(numWeight)) return { valid: false, message: 'Please enter a valid weight' };
  if (numWeight < 20) return { valid: false, message: 'Weight seems too low' };
  if (numWeight > 300) return { valid: false, message: 'Weight seems too high' };
  return { valid: true, message: '' };
};

/**
 * Validate height (cm)
 */
export const validateHeight = (height) => {
  const numHeight = Number(height);
  if (isNaN(numHeight)) return { valid: false, message: 'Please enter a valid height' };
  if (numHeight < 50) return { valid: false, message: 'Height seems too low' };
  if (numHeight > 280) return { valid: false, message: 'Height seems too high' };
  return { valid: true, message: '' };
};

/**
 * Validate waist (cm)
 */
export const validateWaist = (waist) => {
  if (!waist) return { valid: true, message: '' }; // Optional field
  const numWaist = Number(waist);
  if (isNaN(numWaist)) return { valid: false, message: 'Please enter a valid waist measurement' };
  if (numWaist < 30) return { valid: false, message: 'Waist measurement seems too low' };
  if (numWaist > 200) return { valid: false, message: 'Waist measurement seems too high' };
  return { valid: true, message: '' };
};
